import EmptyState from '@bugbug/core/components/EmptyState';
import Loader from '@bugbug/core/components/Loader';
import SearchInput from '@bugbug/core/components/SearchInput';
import { KEY_BINDINGS } from '@bugbug/core/constants/keyBindings';
import { isNumber } from '@bugbug/core/utils/toolbox';
import { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardEventHandler from 'react-keyboard-event-handler';

import { Header, Content } from '~/components/modals/Modal';
import useModal from '~/hooks/useModal';
import { useGetComponentsQuery } from '~/modules/components/components.api';
import { selectSingleProjectId } from '~/modules/project/project.selectors';
import { useAppSelector } from '~/modules/store';
import { usePlaybackCursorPosition } from '~/modules/tests/hooks/usePlaybackCursorPosition/usePlaybackCursorPosition';
import { useInsertComponentMutation } from '~/modules/tests/tests.api';
import { isInternalServerError } from '~/services/api/utils';
import { ACTIONS, ENTITIES, showInternalServerError } from '~/services/toasts/internalServerError';

import * as S from './InsertComponentModal.styled';

interface InsertComponentModalProps {
  testId: string;
  atIndex: number;
}

const InsertComponentModal = ({ testId, atIndex }: InsertComponentModalProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'insertComponentModal' });
  const projectId = useAppSelector(selectSingleProjectId);
  const componentsList = useGetComponentsQuery(projectId, { skip: !projectId });
  const [insertComponent, insertComponentState] = useInsertComponentMutation();
  const modal = useModal();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLUListElement>(null);
  const selectedComponentIndex = useRef();
  const [query, setQuery] = useState('');
  const { refreshPlaybackCursorPosition } = usePlaybackCursorPosition();

  const searchResults = useMemo(
    () =>
      componentsList.data?.filter(({ name }) => name.toLowerCase().includes(query.toLowerCase())) ??
      [],
    [componentsList, query],
  );

  const handleQueryChange = useCallback((event) => setQuery(event.target.value), []);

  const refresh = () => {
    componentsList.refetch();
  };

  const handleSubmit = useCallback(async () => {
    if (!isNumber(selectedComponentIndex.current)) return;

    const group = searchResults[selectedComponentIndex.current];
    try {
      await insertComponent({ testId, componentId: group.id, atIndex }).unwrap();
      refreshPlaybackCursorPosition();
      modal.hide();
    } catch (error) {
      console.error(error);
      showInternalServerError(ENTITIES.GROUP, ACTIONS.INSERT, error as Error);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, modal.hide, insertComponent, testId, atIndex, refreshPlaybackCursorPosition]);

  const handleResultClick = (index) => (event) => {
    selectedComponentIndex.current = index;
    if (!event.target.disabled) {
      handleSubmit();
    }
  };

  const handleNavigation = useCallback((key, event) => {
    if (!searchInputRef.current || !resultsRef.current) return;

    const isDownPressed = key === KEY_BINDINGS.ARROW_DOWN;
    let nextElement;

    if (event.target === searchInputRef.current) {
      nextElement = resultsRef.current.children.item(0);
    } else {
      nextElement = isDownPressed
        ? event.target.nextElementSibling
        : event.target.previousElementSibling;
    }

    (nextElement || searchInputRef.current).focus();
    selectedComponentIndex.current = nextElement ? nextElement.value : null;
  }, []);

  const handleKeyEvent = useCallback(
    (key: string, event: KeyboardEvent) => {
      switch (key) {
        case KEY_BINDINGS.ENTER:
          handleSubmit();
          break;
        case KEY_BINDINGS.ARROW_DOWN:
        case KEY_BINDINGS.ARROW_UP:
          handleNavigation(key, event);
          break;
        default:
          break;
      }
    },
    [handleNavigation, handleSubmit],
  );

  const hasServerError =
    isInternalServerError(insertComponentState.error) ||
    isInternalServerError(componentsList.error);

  return (
    <S.Container data-testid="InsertComponentModal">
      <Header>{t('title', 'Insert existing component')}</Header>
      <Content>
        {hasServerError && <S.ErrorInfo isVisible onRetry={refresh} />}

        {!hasServerError && (
          <KeyboardEventHandler
            handleKeys={[KEY_BINDINGS.ARROW_DOWN, KEY_BINDINGS.ARROW_UP, KEY_BINDINGS.ENTER]}
            onKeyEvent={handleKeyEvent}
          >
            <SearchInput ref={searchInputRef} onChange={handleQueryChange} fullWidth autoFocus />

            {!componentsList.isLoading && (!query || searchResults.length > 0) && (
              <S.Results data-testid="InsertComponentModal.Results" ref={resultsRef}>
                {searchResults.map(({ name, id }, index) => (
                  <S.Result
                    value={index}
                    tabIndex={index + 1}
                    key={id}
                    onClick={handleResultClick(index)}
                  >
                    {name}
                  </S.Result>
                ))}
              </S.Results>
            )}
            {!componentsList.isLoading && query && !searchResults.length && (
              <EmptyState isVisible inline text={t('noData', 'No components were found')} />
            )}
          </KeyboardEventHandler>
        )}

        {!componentsList.isLoading && !hasServerError && !query && !searchResults.length && (
          <EmptyState
            isVisible
            inline
            text={t(
              'placeholder',
              "You don't have any components yet. Components are groups of steps that are shared across multiple tests. When you edit a component you change all related tests at once.",
            )}
          />
        )}

        {componentsList.isLoading && (
          <S.LoaderContainer>
            <Loader />
          </S.LoaderContainer>
        )}
      </Content>
    </S.Container>
  );
};

export default InsertComponentModal;
